<template>
  <div class="holiday-master">
    <CCard>
      <CCardHeader>
        <strong>祝日マスタ</strong>
        <p class="holiday-master__lead">
          全社共通の祝日です。ここが埋まっていれば、契約企業は祝日を入力しなくて済みます。
          <b>逆に、切れた翌日から全社の祝日が平日になります。</b>
        </p>
      </CCardHeader>

      <CCardBody>
        <div v-if="loading" class="holiday-master__loading">読み込んでいます…</div>

        <div v-else>
          <!-- ここが本体。取り込む手段より、切れかけに気づけることのほうが効く。 -->
          <section
            class="holiday-master__stock"
            :class="{ 'holiday-master__stock--warn': status.warn }"
          >
            <p class="holiday-master__kicker">STOCK</p>
            <p v-if="!status.lastDate" class="holiday-master__stock-main">
              <strong>1件も入っていません。</strong>
              いま全社で祝日が平日として扱われています。
            </p>
            <p v-else class="holiday-master__stock-main">
              <strong>{{ status.lastDate }} まで</strong>
              入っています（残り {{ status.daysRemaining }} 日 / 全 {{ status.totalCount }} 件）。
            </p>
            <p v-if="status.warn" class="holiday-master__warn">
              <b>在庫が残り{{ status.warnWithinDays }}日を切りました。</b>
              内閣府は翌年ぶんを前年の2月頃に公表します。下から取り込んでください。
            </p>
          </section>

          <section class="holiday-master__years">
            <p class="holiday-master__kicker">BY YEAR</p>
            <table v-if="status.years && status.years.length" class="holiday-master__table">
              <thead>
                <tr><th>年</th><th>件数</th><th>最初</th><th>最後</th></tr>
              </thead>
              <tbody>
                <tr v-for="row in status.years" :key="row.year">
                  <td>{{ row.year }}</td>
                  <td>{{ row.count }}</td>
                  <td>{{ row.firstDate }}</td>
                  <td>{{ row.lastDate }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else>まだ1件も入っていません。</p>
          </section>

          <section class="holiday-master__import">
            <p class="holiday-master__kicker">IMPORT</p>
            <h3>内閣府のCSVを取り込む</h3>
            <p>
              内閣府が公表している <code>syukujitsu.csv</code> の中身を貼り付けてください。
              1955年から翌年ぶんまで入っているので、<b>年に1回、1回の貼り付けで数年先まで埋まります。</b>
            </p>
            <p class="holiday-master__note">
              <b>文字コードは Shift_JIS です。</b>メモ帳やExcelで開いて、
              文字化けしていない状態のものを貼ってください。
              日付が化けることはないので、<b>化けたまま取り込むと休日一覧に化けた名前が並びます。</b>
            </p>

            <textarea
              v-model="csv"
              class="holiday-master__textarea"
              rows="8"
              placeholder="国民の祝日・休日月日,国民の祝日・休日名称&#10;2026/1/1,元日&#10;2026/1/12,成人の日"
              :disabled="busy"
            ></textarea>

            <div class="holiday-master__actions">
              <CButton color="secondary" :disabled="busy || !csv.trim()" @click="tryImport">
                試す（入れない）
              </CButton>
              <CButton
                color="primary"
                :disabled="busy || !preview"
                @click="applyImport"
              >
                この内容で取り込む
              </CButton>
            </div>

            <div v-if="preview" class="holiday-master__preview">
              <p>
                <strong>{{ preview.parsedCount }} 件</strong>
                読み取れました（{{ preview.firstDate }} 〜 {{ preview.lastDate }}）。
              </p>
              <table class="holiday-master__table">
                <tbody>
                  <tr v-for="(row, i) in preview.sample" :key="i">
                    <td>{{ row.date }}</td>
                    <td>{{ row.name }}</td>
                  </tr>
                </tbody>
              </table>
              <p class="holiday-master__note">先頭と末尾だけを出しています。</p>
            </div>
          </section>

          <p v-if="errorMessage" class="holiday-master__error">{{ errorMessage }}</p>
          <p v-if="successMessage" class="holiday-master__success">{{ successMessage }}</p>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import api from '@/services/nationalHolidayMaster'

export default {
  name: 'NationalHolidayMaster',

  data () {
    return {
      loading: true,
      busy: false,
      csv: '',
      preview: null,
      status: {},
      errorMessage: '',
      successMessage: ''
    }
  },

  created () {
    this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.errorMessage = ''
      try {
        this.status = await api.status()
      } catch (error) {
        this.errorMessage = '祝日マスタの状態を読み込めませんでした。'
          + ' 時間をおいて開き直してください。'
      } finally {
        this.loading = false
      }
    },

    /**
     * まず試す。
     *
     * **件数だけでは貼り間違いに気づけない。**先頭と末尾を出して、
     * 目で確かめてから入れてもらう。
     */
    async tryImport () {
      if (this.busy) return
      this.busy = true
      this.errorMessage = ''
      this.successMessage = ''
      this.preview = null
      try {
        this.preview = await api.importCsv(this.csv, false)
      } catch (error) {
        this.errorMessage = this.messageOf(error)
      } finally {
        this.busy = false
      }
    },

    async applyImport () {
      if (this.busy) return
      this.busy = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const result = await api.importCsv(this.csv, true)
        this.successMessage = result.parsedCount + ' 件を取り込みました。'
        this.preview = null
        this.csv = ''
        await this.load()
      } catch (error) {
        this.errorMessage = this.messageOf(error)
      } finally {
        this.busy = false
      }
    },

    /**
     * サーバの言い分をそのまま出す。
     *
     * **「取り込めませんでした」だけでは何を直せばよいか分からない。**
     * どの行が読めなかったのか、名称が長すぎるのか、サーバが持っている。
     */
    messageOf (error) {
      const body = error && error.response && error.response.data
      if (typeof body === 'string' && body.trim()) return body
      if (body && typeof body.message === 'string' && body.message.trim()) {
        return body.message
      }
      return '取り込めませんでした。CSVの中身を確かめてください。'
    }
  }
}
</script>

<style scoped>
.holiday-master__lead { margin: 6px 0 0; color: #5c6873; }
.holiday-master__kicker {
  margin: 0 0 4px;
  font-size: 11px;
  letter-spacing: .12em;
  color: #8a93a2;
}
.holiday-master__stock {
  padding: 14px 16px;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  background: #fbfcfd;
}
.holiday-master__stock--warn { border-color: #f0b849; background: #fff8e8; }
.holiday-master__stock-main { margin: 0; font-size: 15px; }
.holiday-master__warn { margin: 8px 0 0; color: #8a5b00; }
.holiday-master__years { margin-top: 20px; }
.holiday-master__import { margin-top: 24px; }
.holiday-master__table { width: 100%; border-collapse: collapse; }
.holiday-master__table th,
.holiday-master__table td {
  padding: 6px 10px;
  border-bottom: 1px solid #ebedef;
  text-align: left;
}
.holiday-master__textarea {
  width: 100%;
  font-family: monospace;
  border: 1px solid #d8dbe0;
  border-radius: 6px;
  padding: 8px;
}
.holiday-master__actions { display: flex; gap: 10px; margin-top: 10px; }
.holiday-master__preview { margin-top: 16px; }
.holiday-master__note { color: #5c6873; font-size: 13px; }
.holiday-master__error { margin-top: 14px; color: #e55353; }
.holiday-master__success { margin-top: 14px; color: #2eb85c; }
</style>
